/* eslint-disable no-undef */
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Image from 'global/Image';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import If from 'global/ErrorHandlers/If';
import { sanitizeURL } from 'utils/general';
import alertMessage from 'global/AlertMessage';
import { EditProfileCard } from '../edit-profile/styled';
import SectionEmailField from './SectionEmailField';
import SectionPasswordField from './SectionPasswordField';
import SectionOTPField from './SectionOTPField';
import ModalNavigateConfirmation from './ModalNavigateConfirmation';
import ModalSuccess from './ModalSuccess';
import { useForgotPasswordNonLogin } from './hooks/useForgotPasswordNonLogin';
import ModalBlockedReqOTP from './ModalBlockedReqOTP';
import { OTP_LENGTH } from './config';

interface ForgotPasswordProps {
  backLink?: string;
}

function ForgotPasswordSteps({
  backLink = '/login',
}: Readonly<ForgotPasswordProps>) {
  const router = useRouter();
  const {
    email,
    setEmail,
    password,
    setPassword,
    passwordValidation,
    setOtpConfig,
    otpConfig,
    otpData,
    submitNewPassword,
    submitConfirmPassword,
    requestOTP,
    verifyOTP,
    isSubmitting,
    error,
    resetError,
  } = useForgotPasswordNonLogin();
  const [visibleConfirmModal, setVisibleConfirmModal] = useState(false);
  const [successCreatePassword, setSuccessCreatePassword] = useState(false);
  const [showModalBlocked, setShowModalBlocked] = useState(false);

  const [forgotPassStep, setForgotPassStep] =
    useState<
      | 0 // Input Email
      | 1 // Input OTP verification
      | 2 // Input New Password
      | 3 // Re-Input New Password
    >(0);

  const handleBack = () => {
    if (forgotPassStep === 0) {
      router.push(sanitizeURL(backLink));
      return;
    }

    if (forgotPassStep === 2) {
      setVisibleConfirmModal(true);
      return;
    }

    // @ts-expect-error
    setForgotPassStep(forgotPassStep - 1);
  };

  const submitEmail = async () => {
    try {
      await requestOTP(() => setForgotPassStep(1));
    } catch (e) {
      if (e?.cause?.errorType === 'OTP_LIMIT_EXCEEDED') {
        setShowModalBlocked(true);
      } else {
        alertMessage({
          content: e?.cause?.message,
          messageType: 'error',
          alertType: 'snackbar',
        });
      }
    }
  };

  useEffect(() => {
    resetError();
  }, [forgotPassStep]);

  const renderCardTitle = () => (
    <Flex align="center" style={{ gap: 8 }}>
      <If condition={forgotPassStep > 0}>
        <Image
          src="icon/chevron-left.svg"
          onClick={handleBack}
          size="12px"
          style={{ cursor: 'pointer' }}
          data-cy="change-index-back-button"
        />
      </If>
      <Text weight="700" size="18px">
        Forgot Password
      </Text>
    </Flex>
  );

  return (
    <EditProfileCard
      variant="profile-config"
      title={renderCardTitle()}
      id="setting-password"
      data-cy="change-index-form"
    >
      <ModalBlockedReqOTP visible={showModalBlocked} title={error} />
      <ModalNavigateConfirmation
        visible={visibleConfirmModal}
        onStay={() => setVisibleConfirmModal(false)}
        onLeave={() => router.push('/login')}
      />
      <ModalSuccess
        visible={successCreatePassword}
        onLogout={() => router.push('/login')}
      />
      <If condition={forgotPassStep === 0}>
        <SectionEmailField
          title="Enter Your Email"
          subTitle="Enter your registered email address on Stockbit to create new password"
          onSubmit={submitEmail}
          preventSubmit={isSubmitting || email === ''}
          inputPlaceHolder="Enter Registered Email"
          submitText="Continue"
          email={email}
          setEmail={setEmail}
        />
      </If>
      <If condition={forgotPassStep === 1}>
        <SectionOTPField
          setOtpConfig={setOtpConfig}
          otpLength={OTP_LENGTH}
          otpConfig={otpConfig}
          otpData={otpData}
          preventSubmit={isSubmitting || otpConfig.otp?.length < OTP_LENGTH}
          onSubmit={() => verifyOTP(() => setForgotPassStep(2))}
          onRequestOTP={requestOTP}
          requestOTPOnload={false}
          multipleOTPMethodAvailable={false}
          submitText="Continue"
          error={error}
          resetError={resetError}
        />
      </If>
      <If condition={forgotPassStep === 2}>
        <SectionPasswordField
          title="Create New Password"
          subTitle="Create a strong password with a
          mix of numbers & uppercase and lowercase letters."
          passwordValidation={passwordValidation}
          password={password.new}
          onChangePassword={(n) => setPassword({ ...password, new: n })}
          onSubmit={() => submitNewPassword(() => setForgotPassStep(3))}
          submitText="Continue"
          inputPlaceHolder="Enter New Password"
          preventSubmit={
            isSubmitting ||
            !password.new ||
            !Object.values(passwordValidation).every(
              (value) => value === true,
            ) ||
            Boolean(error)
          }
          error={error}
        />
      </If>
      <If condition={forgotPassStep === 3}>
        <SectionPasswordField
          title="Confirm Your Password"
          subTitle="Enter your password again to confirm."
          onSubmit={() =>
            submitConfirmPassword(() => setSuccessCreatePassword(true))
          }
          submitText="Continue"
          inputPlaceHolder="Re-enter New Password"
          password={password.confirm}
          onChangePassword={(c) => setPassword({ ...password, confirm: c })}
          preventSubmit={isSubmitting || !password.confirm || Boolean(error)}
          error={error}
        />
      </If>
    </EditProfileCard>
  );
}

export default ForgotPasswordSteps;
